import React from 'react';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { calculateHealthStatus, calculateTrendDirection, formatMetricValue } from '../utils/healthCalculator';

interface TekkriMetricCardProps {
  label: string;
  current: number;
  target: number;
  unit: string;
  trend: number[];
  isHigherBetter?: boolean;
  alertCount?: number;
  quickAction?: {
    label: string;
    onClick: () => void;
  };
}

/**
 * TekkriMetricCard Component
 * Displays a metric with health status, sparkline, and trend indicator
 */
const TekkriMetricCard: React.FC<TekkriMetricCardProps> = ({
  label,
  current,
  target,
  unit,
  trend,
  isHigherBetter = true,
  alertCount = 0,
  quickAction
}) => {
  const health = calculateHealthStatus(current, target, isHigherBetter);
  const trendDirection = calculateTrendDirection(trend);
  
  // Prepare data for sparkline
  const sparklineData = trend.map((value, index) => ({
    index,
    value
  }));

  // Get color for sparkline based on health
  const lineColor = health === 'green' ? '#10b981' : health === 'yellow' ? '#f59e0b' : '#ef4444';
  
  // Get status label
  const getStatusLabel = (health: string) => {
    if (health === 'green') return 'On track';
    if (health === 'yellow') return 'At risk';
    return 'Off track';
  };
  
  // Get target prefix
  const targetPrefix = isHigherBetter ? 'above' : 'below';

  return (
    <div className="tekkr-metric-card">
      {/* Health Indicator Dot */}
      <div className={`tekkr-health-indicator ${health}`} />
      
      {/* Card Header */}
      <div className="tekkr-metric-card-header">
        <div className="tekkr-metric-label">{label}</div>
        <div className="tekkr-metric-header-badges">
          <span className={`tekkr-status-badge ${health}`}>
            {getStatusLabel(health)}
          </span>
          {alertCount > 0 && (
            <div className="tekkr-metric-alert-badge">
              🔔 {alertCount} alert{alertCount > 1 ? 's' : ''} active
            </div>
          )}
        </div>
      </div>

      {/* Value and Trend */}
      <div className="tekkr-metric-value-row">
        <div className="tekkr-metric-value">
          {formatMetricValue(current, unit)}
        </div>
        {trendDirection !== 'flat' && (
          <div className={`tekkr-metric-trend ${trendDirection}`}>
            {trendDirection === 'up' ? '↑' : '↓'}
          </div>
        )}
      </div>

      {/* Target */}
      <div className="tekkr-metric-target">
        Target: {targetPrefix} {formatMetricValue(target, unit)}
      </div>

      {/* Sparkline */}
      <div className="tekkr-metric-sparkline">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={sparklineData}>
            <Line
              type="monotone"
              dataKey="value"
              stroke={lineColor}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Quick Action (optional) */}
      {quickAction && (
        <button
          onClick={quickAction.onClick}
          className="tekkr-quick-action"
          style={{ border: 'none', background: 'none', padding: 0 }}
        >
          {quickAction.label} →
        </button>
      )}
    </div>
  );
};

export default TekkriMetricCard;

